import { Inject, Injectable, Optional } from "@angular/core";
import { StatusCodes } from "@obg/ngx.global";
import { Observable } from "rxjs";

import { CoreHttpClient } from "../http-client/http-client.service";

import { CURRENT_LOGIN_SOURCE } from "./auth.const";
import { CurrentLoginSource, LoginSource, LoginWithAuthenticationToken } from "./auth.model";
import { SessionConfigService } from "./session.config";
import { DEVICE_TOKEN_HEADER_KEY, SESSION_TOKEN_HEADER } from "./session.const";
import {
	LoginRequest,
	LoginSuccessfulResponse,
	LoginType,
	LoginWithPersonalNumberRequest,
	LoginWithTokenRequest,
	LoginWithVerificationCodeRequest,
	SessionLimitNextTimeLoginResponse,
	SessionResponse,
	TimeToLiveResponse,
	TwoFactorAuthLogin,
} from "./session.model";

@Injectable({
	providedIn: "root",
})
export class SessionClient {
	private suppressedErrorCodes = [StatusCodes.BadRequest, StatusCodes.Forbidden, StatusCodes.Unauthorized];

	constructor(
		private config: SessionConfigService,
		private httpClient: CoreHttpClient,
		@Optional() @Inject(CURRENT_LOGIN_SOURCE) private loginSource?: CurrentLoginSource
	) {}

	login(request: LoginRequest | TwoFactorAuthLogin): Observable<LoginSuccessfulResponse> {
		if (request.deviceId) {
			this.httpClient.setDefaultHeader(DEVICE_TOKEN_HEADER_KEY, request.deviceId);
		}
		return this.doLogin(request);
	}

	loginWithVerificationCode(request: LoginWithVerificationCodeRequest): Observable<LoginSuccessfulResponse> {
		return this.httpClient.post<LoginSuccessfulResponse>({
			url: this.config.getLoginEndpoint(),
			suppressErrorLogging: this.suppressedErrorCodes,
			data: {
				type: LoginType.VerificationCode,
				loginSource: this.currentLoginSource(),
				...request,
			},
		});
	}

	loginWithPersonalNumber(request: LoginWithPersonalNumberRequest): Observable<LoginSuccessfulResponse> {
		return this.httpClient.post<LoginSuccessfulResponse>({
			url: this.config.getLoginEndpoint(),
			suppressErrorLogging: this.suppressedErrorCodes,
			data: {
				type: LoginType.PersonalNumber,
				loginSource: this.currentLoginSource(),
				...request,
			},
		});
	}

	loginWithToken(request: LoginWithTokenRequest): Observable<LoginSuccessfulResponse> {
		const partialRequest: Partial<Omit<LoginWithTokenRequest, "isOneTimeLoginPassword">> = {
			token: request.token,
			authenticationMethod: request.authenticationMethod,
			iovationBlackBox: request.iovationBlackBox,
			fraudToken: request.fraudToken,
		};

		return this.httpClient.post<LoginSuccessfulResponse>({
			url: this.config.getLoginEndpoint(),
			suppressErrorLogging: this.suppressedErrorCodes,
			data: {
				type: request.isOneTimeLoginPassword ? LoginType.OneTimeToken : LoginType.Token,
				loginSource: this.currentLoginSource(),
				...partialRequest,
			},
		});
	}

	loginWithAuthenticationToken(request: LoginWithAuthenticationToken): Observable<LoginSuccessfulResponse> {
		return this.httpClient.post<LoginSuccessfulResponse>({
			url: this.config.getLoginEndpoint(),
			data: {
				type: "Token",
				loginSource: this.currentLoginSource(),
				token: request.authenticationToken,
				iovationBlackBox: request.iovationBlackBox,
				fraudToken: request.fraudToken,
			},
		});
	}

	logout(sessionToken?: string): Observable<object> {
		return this.httpClient.delete<object>({
			url: this.config.getLogoutEndpoint(),
			headers: sessionToken ? { sessionToken } : undefined,
		});
	}

	getSession(sessionToken: string): Observable<SessionResponse> {
		return this.httpClient.get<SessionResponse>({
			url: this.config.getSessionEndpoint(),
			headers: { sessionToken },
			suppressErrorLogging: this.suppressedErrorCodes,
		});
	}

	keepAlive(sessionToken: string): Observable<TimeToLiveResponse> {
		return this.httpClient.patch<TimeToLiveResponse>({
			url: this.config.getKeepAliveEndpoint(),
			suppressErrorLogging: this.suppressedErrorCodes,
			headers: { sessionToken },
		});
	}

	getNextTimeLoginAfterSessionLimitReached() {
		return this.httpClient.get<SessionLimitNextTimeLoginResponse>({
			url: this.config.getNextTimeLoginEndpoint(),
		});
	}

	setSessionToken(sessionToken: string) {
		this.httpClient.setDefaultHeader(SESSION_TOKEN_HEADER, sessionToken);
	}

	clearSessionToken() {
		this.httpClient.deleteDefaultHeader(SESSION_TOKEN_HEADER);
	}

	private currentLoginSource(): LoginSource {
		return (this.loginSource && this.loginSource.loginSource) || LoginSource.Web;
	}

	private doLogin(request: LoginRequest | TwoFactorAuthLogin): Observable<LoginSuccessfulResponse> {
		return this.httpClient.post<LoginSuccessfulResponse>({
			url: this.config.getLoginEndpoint(),
			suppressErrorLogging: this.suppressedErrorCodes,
			data: {
				type: LoginType.UsernamePassword,
				loginSource: this.currentLoginSource(),
				iovationBlackBox: "",
				fraudToken: "",
				...request,
			},
		});
	}
}
